/**
 * Single source of truth for date and time display formatting on the client.
 * Helpers are grouped by what the caller is rendering: HTML form inputs,
 * absolute timestamps (SGT-anchored), relative time, durations, and blog dates.
 */

type DateValue = Date | string | number | null | undefined;

const SGT_TIMEZONE = 'Asia/Singapore';
const SGT_LOCALE = 'en-SG';
const RELATIVE_JUST_NOW_MS = 5_000;
const BLOG_DATE_FORMATTER = new Intl.DateTimeFormat('en-SG', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

function toDate(value: DateValue): Date | null {
  if (value == null || value === '') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Format a date for HTML date input fields (YYYY-MM-DD), anchored to Singapore time
 * so SGT users always see the local calendar day even when the source value is UTC.
 */
export function formatDateForInput(date: DateValue): string {
  const d = toDate(date);
  if (!d) return '';

  const formatter = new Intl.DateTimeFormat('en-SG', {
    timeZone: SGT_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const byType = Object.fromEntries(
    formatter.formatToParts(d).map(p => [p.type, p.value])
  );
  return `${byType.year}-${byType.month}-${byType.day}`;
}

/**
 * Format a date as a weekday + date + time string for admin tables and detail
 * panels (e.g. "Fri, 15 Mar 2024, 6:30:00 pm"). Returns "—" when missing or
 * invalid so callers don't need null guards.
 *
 * The month is spelled (3-letter) rather than numeric: these timestamps back
 * payment and access-code audit trails, where "1/2/2026" is genuinely ambiguous
 * between D/M and M/D depending on the viewer's browser locale. Anchored to SGT
 * like every other absolute-time helper here, so an admin travelling abroad
 * reads the same wall-clock time the parent paid at.
 */
export function formatDateTime(value: DateValue): string {
  const date = toDate(value);
  if (!date) return '—';
  const weekday = date.toLocaleDateString(SGT_LOCALE, {
    timeZone: SGT_TIMEZONE,
    weekday: 'short',
  });
  const dateTime = date.toLocaleString(SGT_LOCALE, {
    timeZone: SGT_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  return `${weekday}, ${dateTime}`;
}

function formatDateKeySGT(date: Date): string {
  return new Intl.DateTimeFormat(SGT_LOCALE, {
    timeZone: SGT_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function formatTimeSGT(date: Date): string {
  return date
    .toLocaleTimeString(SGT_LOCALE, {
      timeZone: SGT_TIMEZONE,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase();
}

/**
 * Format a date as a friendly absolute time in SGT.
 * Today → "Today, 6:01pm"
 * Yesterday → "Yesterday, 6:01pm"
 * Older → "8 Apr, 6:01pm"
 */
export function formatAbsoluteTimeSGT(date: Date): string {
  const timeStr = formatTimeSGT(date);
  const now = new Date();
  const dateKey = formatDateKeySGT(date);

  if (dateKey === formatDateKeySGT(now)) {
    return `Today, ${timeStr}`;
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dateKey === formatDateKeySGT(yesterday)) {
    return `Yesterday, ${timeStr}`;
  }

  const dayMonth = date.toLocaleDateString(SGT_LOCALE, {
    timeZone: SGT_TIMEZONE,
    day: 'numeric',
    month: 'short',
  });

  return `${dayMonth}, ${timeStr}`;
}

/**
 * Format a date value as an SGT timestamp string ("Today, 6:01pm" etc.).
 * Returns "—" for missing or invalid input.
 */
export function formatTimestamp(value: DateValue): string {
  const date = toDate(value);
  return date ? formatAbsoluteTimeSGT(date) : '—';
}

/**
 * Short human-readable relative time ("just now", "30s ago", "15m ago").
 * Returns null when input is invalid OR when the value is older than one hour,
 * so callers can fall back to {@link formatTimestamp} for absolute display.
 */
export function formatRelativeTime(value: DateValue): string | null {
  const date = toDate(value);
  if (!date) return null;

  const diffMs = Date.now() - date.getTime();
  if (diffMs < RELATIVE_JUST_NOW_MS) return 'just now';

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return `${seconds}s ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  return null;
}

/**
 * Human-readable duration from startedAt to endedAt (or now if not ended yet).
 * Returns "—" for missing/invalid start or for negative durations.
 */
export function formatDuration(
  startedAt: DateValue,
  endedAt?: DateValue
): string {
  const start = toDate(startedAt);
  if (!start) return '—';

  const end = toDate(endedAt) ?? new Date();
  const diffMs = end.getTime() - start.getTime();
  if (diffMs < 0) return '—';

  const totalSeconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes === 0) return `${seconds}s`;
  if (minutes < 60) return `${minutes}m ${seconds}s`;

  const hours = Math.floor(minutes / 60);
  const remainingMins = minutes % 60;
  return `${hours}h ${remainingMins}m`;
}

/** Compact elapsed duration; missing data stays distinct from recorded zero. */
export function formatElapsedMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—';
  const seconds = Math.floor(ms / 1000);
  if (seconds === 0) return '<1s';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes >= 60) return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  return `${minutes}m${seconds % 60 ? ` ${seconds % 60}s` : ''}`;
}

/**
 * Format a YYYY-MM-DD blog publish/update date in UTC as "5 Jan 2024".
 * Anchored to UTC because blog dates are calendar-day metadata, not timestamps.
 */
export function formatBlogDate(isoDate: string): string {
  return BLOG_DATE_FORMATTER.format(new Date(`${isoDate}T00:00:00Z`));
}

export function isPostUpdated(post: {
  publishDate: string;
  updatedDate?: string;
}): boolean {
  return Boolean(post.updatedDate && post.updatedDate !== post.publishDate);
}
